import { useMemo } from "react";
import { Flex } from "styles/utility-components";

import EmptyState from "components/ui/empty_state/EmptyState";
import EditColumnsButton from "lib/core_components/EditColumnsButton";
import { Table } from "lib/core_components/Table";
import { SpendFilters } from "lib/hooks/api/spend/spendFilters";
import { useURLState } from "lib/hooks/useURLState";
import { EventDataTypes } from "lib/tracking";
import { SpendExportGroupBy } from "lib/types/graphQLEnums";
import { ColumnSetting, TableSettings } from "lib/types/models";
import { SpendColumnsSchema, TransactionSortSchema } from "./hooks/useSpendUrlState";
import { DEFAULT_TRANSACTION_SORT, useTransactionsTable } from "./hooks/useTransactionsTable";
import { TRANSACTION_COLUMN_FIELDS, TRANSACTION_COLUMNS, TransactionColumn } from "./columns";
import { TransactionTableRow } from "./config";
import ExportDataButton from "./ExportDataButton";

type Props = {
  /** The page's filters: this pair, over the page's date range. */
  filters: SpendFilters;
  /** Set when the filters cover one chart period, e.g. "Mar 2025", so the empty state names it. */
  periodLabel?: string;
};

/**
 * The relationship's transactions with just a column picker and export: the page's controls
 * decide which transactions these are, so the table carries no filters of its own.
 */
export function BuyerSupplierTransactions({ filters, periodLabel }: Props) {
  const [columnFields, setColumnFields] = useURLState(
    "transactionColumns",
    TRANSACTION_COLUMN_FIELDS,
    SpendColumnsSchema,
    { encoder: "compress", replaceState: true },
  );
  // In the URL like the columns, so a refresh or a shared link keeps the order.
  const [sort, setSort] = useURLState(
    "transactionSort",
    DEFAULT_TRANSACTION_SORT,
    TransactionSortSchema,
    { encoder: "compress", replaceState: true },
  );
  const table = useTransactionsTable(filters, columnFields, {
    tableKey: "transactions-table",
    sort,
    onSortChange: setSort,
  });

  // Memoised: the column picker re-syncs its list whenever this array's identity changes.
  const selectedColumns = useMemo(
    () =>
      columnFields
        .map((field) => TRANSACTION_COLUMNS.find((c) => c.field === field))
        .filter((c): c is ColumnSetting<TransactionColumn> => c !== undefined),
    [columnFields],
  );

  return (
    <Flex column gap={16}>
      <Flex justifyContent="flex-end" alignItems="center" gap={8}>
        <EditColumnsButton<TableSettings<TransactionColumn>>
          allAvailableColumns={TRANSACTION_COLUMNS}
          selectedColumns={selectedColumns}
          onNewColumnSettingChange={(columns) => setColumnFields(columns.map((c) => c.field))}
          dataType={EventDataTypes.spend}
          trackLegacyReorderEvent={false}
        />
        <ExportDataButton
          filters={filters}
          totalRows={table.pagination.total ?? 0}
          isLoadingTotalRows={table.isLoading}
          groupBy={SpendExportGroupBy.None}
          sort={table.sort}
        />
      </Flex>
      <Table
        ariaLabel="Transactions"
        columns={table.columns}
        dataSource={table.data}
        isError={table.isError}
        pagination={{
          ...table.pagination,
          onChange: (page, pageSize) => {
            table.pagination.onChange(page || 1, pageSize);
          },
          showSizeChanger: true,
        }}
        onChange={(_pagination, _filters, sorter) => table.onSortChange(sorter)}
        rowKey={(row) => (row as TransactionTableRow).id}
        loading={table.isLoading}
        locale={{
          emptyText: periodLabel ? (
            <NoTransactionsInPeriod periodLabel={periodLabel} />
          ) : (
            <NoTransactions inPeriod={Boolean(filters.dateFrom)} />
          ),
        }}
      />
    </Flex>
  );
}

/** Worded like the chart's empty state, so the two agree when a pair has no spend. */
function NoTransactions({ inPeriod }: { inPeriod: boolean }) {
  return (
    <EmptyState
      type="noResults"
      heading={inPeriod ? "No transactions in this period" : "No transactions yet"}
      description={
        inPeriod
          ? "Nothing was invoiced between this buyer and supplier in this period. Try a longer date range."
          : "This buyer hasn't published any spend with this supplier."
      }
      width="100%"
      yAxisMargin="64px"
    />
  );
}

/** A single chart period has no longer range to suggest, so it just says what's missing. */
function NoTransactionsInPeriod({ periodLabel }: { periodLabel: string }) {
  return (
    <EmptyState
      type="noResults"
      heading={`No transactions in ${periodLabel}`}
      description={`Nothing was invoiced between this buyer and supplier in ${periodLabel}.`}
      width="100%"
      yAxisMargin="64px"
    />
  );
}
